/**
 * SpeedDatingHubPage.jsx
 * 
 * Single entry point for Speed Dating with two clear "doors":
 * 1. Text Speed Dating (Always Open) - 5-min rounds
 * 2. Video Speed Dating (Scheduled) - Daily per region
 * 
 * Rules:
 * - Never interrupt Text mid-round with video prompts
 * - Show video "next up" only on this hub, not during active sessions
 */

import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import apiClient from "../api/client";
import {
  MessageCircle,
  Video,
  Clock,
  MapPin,
  ArrowRight,
  ArrowLeft,
  ChevronDown,
  Sparkles,
  Users,
  Heart,
  Shield,
} from "lucide-react";
import { trackEvent } from "../api/analytics";


// Region configuration
const VIDEO_REGIONS = {
  us: { name: "United States", flag: "🇺🇸", timezone: "America/Chicago" },
  south_america_islands: { name: "South America & Caribbean", flag: "🌴", timezone: "America/Sao_Paulo" },
  africa: { name: "Africa", flag: "🌍", timezone: "Africa/Lagos" },
};

// =============================================================================
// REGION DROPDOWN
// =============================================================================

function RegionDropdown({ selected, onSelect }) {
  const [isOpen, setIsOpen] = useState(false);
  const region = VIDEO_REGIONS[selected];

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-700/50 text-white/80 text-sm hover:bg-slate-700 transition-colors"
        data-testid="region-dropdown"
      >
        <span>{region?.flag}</span>
        <span>{region?.name}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-56 bg-slate-800 border border-slate-700 rounded-xl shadow-xl z-20 py-1">
          {Object.entries(VIDEO_REGIONS).map(([key, r]) => (
            <button
              key={key}
              onClick={() => {
                onSelect(key);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-2 text-left flex items-center gap-2 hover:bg-slate-700 transition-colors ${
                key === selected ? "bg-purple-500/20 text-purple-300" : "text-white/70"
              }`}
              data-testid={`region-${key}`}
            >
              <span>{r.flag}</span>
              <span className="text-sm">{r.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// TEXT SPEED DATING CARD
// =============================================================================

function TextSpeedDatingCard({ onClick }) {
  return (
    <div
      onClick={onClick}
      className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-pink-500/20 via-slate-900 to-slate-900 border border-pink-500/30 p-6 cursor-pointer hover:border-pink-400/50 hover:shadow-lg hover:shadow-pink-500/10 transition-all"
      data-testid="text-speed-dating-card"
    >
      {/* Glow effect */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-pink-500/20 rounded-full blur-3xl group-hover:bg-pink-500/30 transition-colors" />

      {/* Status badge */}
      <div className="flex items-center gap-2 mb-4">
        <span className="px-3 py-1 rounded-full bg-green-500/20 border border-green-500/50 text-green-400 text-xs font-medium flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          Open Now
        </span>
      </div>

      {/* Icon */}
      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
        <MessageCircle className="w-7 h-7 text-white" />
      </div>

      {/* Content */}
      <h3 className="text-xl font-bold text-white mb-2">Text Speed Dating</h3>
      <p className="text-white/60 text-sm mb-4">
        Private 1:1 text chats • 5-minute rounds • Always available
      </p>

      {/* Features */}
      <div className="flex flex-wrap gap-2 mb-5">
        <span className="px-2 py-1 rounded-md bg-slate-800 text-white/50 text-xs flex items-center gap-1">
          <Clock className="w-3 h-3" /> 5 min rounds
        </span>
        <span className="px-2 py-1 rounded-md bg-slate-800 text-white/50 text-xs flex items-center gap-1">
          <Users className="w-3 h-3" /> 1:1 private
        </span>
        <span className="px-2 py-1 rounded-md bg-slate-800 text-white/50 text-xs flex items-center gap-1">
          <Heart className="w-3 h-3" /> Like/Pass
        </span>
      </div>

      {/* CTA */}
      <button className="w-full py-3 rounded-xl bg-gradient-to-r from-pink-500 to-rose-600 text-white font-semibold flex items-center justify-center gap-2 group-hover:from-pink-600 group-hover:to-rose-700 transition-all">
        Start Text Date
        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
      </button>
    </div>
  );
}

// =============================================================================
// VIDEO SPEED DATING CARD
// =============================================================================

function VideoSpeedDatingCard({ onClick, selectedRegion, onRegionChange, nextEvent }) {
  // Format next event time
  const formatNextTime = () => {
    if (!nextEvent?.scheduled_start) return "8:00 PM";
    
    try {
      const date = new Date(nextEvent.scheduled_start);
      const regionTz = VIDEO_REGIONS[selectedRegion]?.timezone || "America/Chicago";
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        timeZone: regionTz,
        timeZoneName: "short",
      });
    } catch {
      return "8:00 PM";
    }
  };

  // Status based on event
  const getStatus = () => {
    if (!nextEvent) return { label: "Scheduled", color: "amber", icon: Clock };
    
    switch (nextEvent.status) {
      case "live":
        return { label: "LIVE NOW", color: "green", icon: Sparkles, pulse: true };
      case "join_open":
        return { label: "Joining Open", color: "green", icon: Users };
      case "scheduled":
        return { label: "Upcoming", color: "amber", icon: Clock };
      default:
        return { label: "Scheduled", color: "amber", icon: Clock };
    }
  };

  const status = getStatus();

  return (
    <div
      className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-500/20 via-slate-900 to-slate-900 border border-purple-500/30 p-6 cursor-pointer hover:border-purple-400/50 hover:shadow-lg hover:shadow-purple-500/10 transition-all"
      data-testid="video-speed-dating-card"
    >
      {/* Glow effect */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/20 rounded-full blur-3xl group-hover:bg-purple-500/30 transition-colors" />

      {/* Status badge */}
      <div className="flex items-center gap-2 mb-4">
        <span className={`px-3 py-1 rounded-full bg-${status.color}-500/20 border border-${status.color}-500/50 text-${status.color}-400 text-xs font-medium flex items-center gap-1`}>
          {status.pulse && <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />}
          {!status.pulse && <status.icon className="w-3 h-3" />}
          {status.label}
        </span>
        <span className="px-3 py-1 rounded-full bg-blue-500/20 border border-blue-500/50 text-blue-300 text-xs font-medium flex items-center gap-1">
          <Shield className="w-3 h-3" />
          Verified Only
        </span>
      </div>

      {/* Icon */}
      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
        <Video className="w-7 h-7 text-white" />
      </div>

      {/* Content */}
      <h3 className="text-xl font-bold text-white mb-2">Video Speed Dating</h3>
      <p className="text-white/60 text-sm mb-4">
        Face-to-face connections • Daily events • By region
      </p>

      {/* Next event info */}
      <div className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-slate-800/50 border border-slate-700/50">
        <div className="flex-1">
          <p className="text-white/50 text-xs mb-1">Next event</p>
          <p className="text-white font-medium text-sm flex items-center gap-1">
            <Clock className="w-4 h-4 text-purple-400" />
            {formatNextTime()}
          </p>
        </div>
        <div onClick={(e) => e.stopPropagation()}>
          <RegionDropdown selected={selectedRegion} onSelect={onRegionChange} />
        </div>
      </div>

      {/* Features */}
      <div className="flex flex-wrap gap-2 mb-5">
        <span className="px-2 py-1 rounded-md bg-slate-800 text-white/50 text-xs flex items-center gap-1">
          <Video className="w-3 h-3" /> Face-to-face
        </span>
        <span className="px-2 py-1 rounded-md bg-slate-800 text-white/50 text-xs flex items-center gap-1">
          <MapPin className="w-3 h-3" /> 3 regions
        </span>
        <span className="px-2 py-1 rounded-md bg-slate-800 text-white/50 text-xs flex items-center gap-1">
          <Clock className="w-3 h-3" /> 8 PM daily
        </span>
      </div>

      {/* CTA */}
      <button
        onClick={onClick}
        className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-semibold flex items-center justify-center gap-2 group-hover:from-purple-600 group-hover:to-indigo-700 transition-all"
      >
        View Schedule
        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
      </button>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function SpeedDatingHubPage() {
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);

  // State
  const [selectedRegion, setSelectedRegion] = useState("us");
  const [nextVideoEvent, setNextVideoEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  // Detect user's region
  useEffect(() => {
    if (user?.preferred_region_key && VIDEO_REGIONS[user.preferred_region_key]) {
      setSelectedRegion(user.preferred_region_key);
    } else {
      // Infer from browser timezone
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz?.includes("America") && !tz.includes("Sao_Paulo") && !tz.includes("Buenos_Aires")) {
        setSelectedRegion("us");
      } else if (tz?.includes("Sao_Paulo") || tz?.includes("Buenos_Aires") || tz?.includes("Caribbean")) {
        setSelectedRegion("south_america_islands");
      } else if (tz?.includes("Africa")) {
        setSelectedRegion("africa");
      }
    }
  }, [user?.preferred_region_key]);

  // Fetch next video event for selected region
  useEffect(() => {
    const fetchNextEvent = async () => {
      if (!token) return;
      
      setLoading(true);
      try {
        const res = await apiClient.get(`/speed-dating/video/current`, {
          params: { region_key: selectedRegion },
          headers: { Authorization: `Bearer ${token}` },
        });
        setNextVideoEvent(res.data.event);
      } catch (err) {
        console.error("[SpeedDatingHub] Failed to fetch video event:", err);
        setNextVideoEvent(null);
      } finally {
        setLoading(false);
      }
    };

    fetchNextEvent();
  }, [selectedRegion, token]);

  // Handlers
  const handleTextClick = () => {
    trackEvent("speed_dating_hub_text_clicked");
    navigate("/text-speed-dating");
  };

  const handleVideoClick = () => {
    trackEvent("speed_dating_hub_video_clicked", { region_key: selectedRegion });
    navigate("/speed-dating/video");
  };

  const handleRegionChange = (region) => {
    setSelectedRegion(region);
    trackEvent("speed_dating_hub_region_changed", { region_key: region });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-slate-950 to-black">
      {/* Header with back button */}
      <header className="px-4 py-6">
        <div className="max-w-2xl mx-auto">
          {/* FIX #4: Back button to exit Speed Dating */}
          <button
            onClick={() => navigate("/dashboard")}
            className="mb-4 inline-flex items-center gap-1 text-white/50 hover:text-white/70 text-sm transition-colors"
            data-testid="speed-dating-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Dashboard</span>
          </button>
          
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
              Speed Dating
            </h1>
            <p className="text-white/60 text-sm sm:text-base">
              Two ways to connect. Choose your vibe.
            </p>
          </div>
        </div>
      </header>

      {/* Cards */}
      <main className="max-w-4xl mx-auto px-4 pb-12">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Text Speed Dating */}
          <TextSpeedDatingCard onClick={handleTextClick} />

          {/* Video Speed Dating */}
          <VideoSpeedDatingCard
            onClick={handleVideoClick}
            selectedRegion={selectedRegion}
            onRegionChange={handleRegionChange}
            nextEvent={nextVideoEvent}
          />
        </div>

        {/* Comparison */}
        <div className="mt-10 bg-slate-900/50 rounded-2xl border border-slate-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4 text-center">
            What's the difference?
          </h2>
          <div className="grid md:grid-cols-2 gap-6 text-sm">
            <div>
              <h3 className="text-pink-400 font-medium mb-2 flex items-center gap-2">
                <MessageCircle className="w-4 h-4" /> Text Speed Dating
              </h3>
              <ul className="space-y-2 text-white/60">
                <li>✓ Available 24/7, start anytime</li>
                <li>✓ Text-only, low pressure</li>
                <li>✓ 5-minute rounds with auto-end</li>
                <li>✓ Perfect for introverts</li>
                <li>✓ No camera needed</li>
              </ul>
            </div>
            <div>
              <h3 className="text-purple-400 font-medium mb-2 flex items-center gap-2">
                <Video className="w-4 h-4" /> Video Speed Dating
              </h3>
              <ul className="space-y-2 text-white/60">
                <li>✓ Face-to-face connection</li>
                <li>✓ Daily at 8 PM (your region)</li>
                <li>✓ Age-verified participants only</li>
                <li>✓ Multiple rounds per event</li>
                <li>✓ More immersive experience</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Quick tips */}
        <div className="mt-6 text-center text-white/40 text-xs">
          <p>
            Tip: Start with text to warm up, then join video when you're ready for face-to-face.
          </p>
        </div>
      </main>
    </div>
  );
}
